// Platform logout helpers for PWA tenant isolation.
//
// The PWA can't terminate a WorkOS AuthKit session on its own — that cookie
// lives on the WorkOS-hosted domain. Real logout has to round-trip through
// platform's `/v1/auth/logout`, which calls WorkOS's SDK on the server and
// redirects the browser through WorkOS's hosted `/sessions/logout` back to
// the PWA unauthenticated.
//
// This module centralizes the URL construction and localStorage teardown so
// the PWA `UserMenu` and `/auth/reset` screens can't drift apart.

const ENTERPRISE_PREFIX = 'khal:enterprise:';
const ENTERPRISE_LAST_USED_KEY = 'khal:enterprise:_lastUsed';

// Matches every localStorage key under the `khal` namespace — both the
// colon-separated enterprise config (`khal:enterprise:*`) and the
// hyphen-separated zustand/persist stores (`khal-windows:*`,
// `khal-desktop-tabs:*`, `khal-os:*`, `khal-theme-mode`, …). Widened from the
// original `khal:enterprise:*` scope so sign-out also wipes window/tab state
// and per-app preferences — leaving those behind would defeat the
// tenant-isolation invariant after a kernel swap.
const KHAL_NAMESPACE_RE = /^khal[-:]/;

/**
 * Read the JWT for the currently-active kernel. Returns `null` when no kernel
 * is selected yet or the stored config is unparseable. Called by logout
 * handlers so the platform `/v1/auth/logout` route can extract the `sid`
 * claim and target the right WorkOS session.
 */
export function getActiveKernelToken(): string | null {
	if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') {
		return null;
	}
	const lastUsed = window.localStorage.getItem(ENTERPRISE_LAST_USED_KEY);
	if (!lastUsed) return null;
	const raw = window.localStorage.getItem(`${ENTERPRISE_PREFIX}${lastUsed}`);
	if (!raw) return null;
	try {
		const parsed = JSON.parse(raw) as { token?: unknown };
		return typeof parsed.token === 'string' && parsed.token.length > 0 ? parsed.token : null;
	} catch {
		return null;
	}
}

/**
 * Purge every localStorage entry under the `khal` namespace. Matches both
 * `khal-*` (zustand stores, theme, recent apps) and `khal:*` (enterprise
 * config) prefixes.
 *
 * This is the single utility both the `UserMenu` PWA logout branch and the
 * `browserAdapter.clearEnterpriseConfig` method delegate to — any new
 * `khal-`/`khal:` localStorage consumer is automatically covered at sign-out
 * without touching the logout code paths.
 */
export function clearAllKhalStorage(): void {
	if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') return;
	const toDelete: string[] = [];
	for (let i = 0; i < window.localStorage.length; i++) {
		const key = window.localStorage.key(i);
		if (key && KHAL_NAMESPACE_RE.test(key)) toDelete.push(key);
	}
	for (const key of toDelete) window.localStorage.removeItem(key);
}

export interface BuildPlatformLogoutUrlArgs {
	/** Platform origin, e.g. `https://platform.khal.ai`. Trailing slash tolerated. */
	platformUrl: string;
	/** PWA origin to land on after WorkOS finishes the logout round-trip. */
	pwaOrigin: string;
	/** Current kernel-session JWT, if one is available. Optional — without it
	 *  platform still validates the origin and 302s, but cannot revoke the
	 *  WorkOS session (`sid` is in the JWT claims). */
	token?: string | null;
}

/**
 * Compose the `GET /v1/auth/logout` URL on platform. The route validates
 * `returnTo` against `PLATFORM_PWA_ORIGINS` and, when a token is present and
 * carries the `sid` claim, redirects through WorkOS's hosted
 * `/sessions/logout` so the AuthKit cookie is actually terminated.
 */
export function buildPlatformLogoutUrl({
	platformUrl,
	pwaOrigin,
	token,
}: BuildPlatformLogoutUrlArgs): string {
	const base = platformUrl.replace(/\/+$/, '');
	const params = new URLSearchParams({ returnTo: pwaOrigin });
	if (token) params.set('token', token);
	return `${base}/v1/auth/logout?${params.toString()}`;
}
